import { AgentDevice } from '@models/agent-device.model';
import { Agent } from '@models/agent.model';
import { Page } from '@models/page';
import { ReportAgentContext, isAgentActive } from '@modules/agent/report-agent';
import { LabType } from '@modules/test-development/components/run-results/machine-config/lab-type';
import { DeviceType } from '@modules/test-development/components/run-results/machine-config/machine/device-type';
import { OSType } from '@modules/test-development/components/run-results/machine-config/machine/os-type';
import { AgentDeviceService } from '@services/agent-device.service';
import { AgentService } from '@services/agent.service';
import { TestDevice } from '@testsigmainc/models';
import { Platform } from '@testsigmainc/constants';
import { useContext, useEffect, useState } from 'react';

import { Feature } from '@enums/feature.enum';
import { UseBaseHooks } from '@hooks/use-base.hooks';
import testCaseParallel from '@images/testcase-parallel.svg';
import testSuiteParallel from '@images/testsuite-parallel.svg';
import { TestPlanLabType } from '@modules/test-development/enums';
import { ToolTip, useTranslation } from '@testsigma/atoms';
import classNames from 'classnames';

interface MachineProps {
  testDevice: TestDevice;
  variant?: 'detail' | 'concise';
  className?: string;
  showOsType?: boolean;
  showDeviceType?: boolean;
  showParallelLine?: boolean;
  testPlanId?: string | number;
  hideLaptypeIcon?: boolean;
}

interface ParallelLineProps {
  testDevice: TestDevice;
}

const ParallelLine: React.FC<ParallelLineProps> = ({ testDevice }) => {
  const { t } = useTranslation();
  const { hasFeature } = UseBaseHooks();
  const isParallelRunSupported =
    !testDevice?.isHybrid ||
    (hasFeature(Feature.LOCAL_PARALLEL_RUN) &&
      testDevice?.platformDevice?.platform != Platform.iOS &&
      testDevice?.platformDevice?.platform != Platform.Android);
  const hasLine =
    (testDevice.runTestCasesInParallel || testDevice.runTestSuitesInParallel) &&
    isParallelRunSupported;

  return (
    hasLine && (
      <ToolTip
        content={t(
          testDevice.runTestCasesInParallel
            ? 'test-machine.cases-parallel'
            : 'test-machine.suites-parallel',
        )}
      >
        <img
          alt={t(
            testDevice.runTestCasesInParallel
              ? 'test-machine.cases-parallel'
              : 'test-machine.suites-parallel',
          )}
          src={
            testDevice.runTestCasesInParallel
              ? testCaseParallel
              : testSuiteParallel
          }
          className='h-[14px] mr-3'
        />
      </ToolTip>
    )
  );
};

export const Machine: React.FC<MachineProps> = ({
  testDevice,
  variant = 'concise',
  className = '',
  showOsType = true,
  showDeviceType = true,
  showParallelLine = false,
  testPlanId,
  hideLaptypeIcon = false,
}) => {
  const [agent, setAgent] = useState(new Agent());
  const [agentDevice, setAgentDevice] = useState(new AgentDevice());
  const { setTestPlanIdVsAgents } = useContext(ReportAgentContext);

  useEffect(() => {
    fetchAgentAndDevice(setAgent, setAgentDevice, testDevice);
  }, [testDevice]);

  useEffect(() => {
    const { uniqueId: agentUniqueId } = agent;
    if (isAgentActive(agent) && agentUniqueId) {
      setTestPlanIdVsAgents((prev) => {
        if (!prev[testPlanId]) {
          prev[testPlanId] = [];
        }
        if (
          !prev[testPlanId].find(({ uniqueId }) => uniqueId === agentUniqueId)
        ) {
          return {
            ...prev,
            [testPlanId]: [...prev[testPlanId], agent],
          };
        }
        return prev;
      });
    }
  }, [agent, setTestPlanIdVsAgents, testPlanId]);

  return (
    <div
      className={classNames(
        'flex flex-row',
        {
          'gap-x-4': variant == 'detail',
          'gap-x-3': variant == 'concise',
        },
        className,
      )}
    >
      <LabType
        className={variant == 'detail' ? 'max-w-[118px]' : ''}
        labType={testDevice.executionLabType}
        agent={agent}
        variant={variant}
        hideIcon={hideLaptypeIcon}
      />
      {showOsType &&
        testDevice.executionLabType != TestPlanLabType.PrivateGrid && (
          <OSType
            testDevice={testDevice}
            agent={agent}
            agentDevice={agentDevice}
            className={classNames({
              'max-w-[60px]': variant == 'detail',
            })}
          />
        )}
      {showDeviceType && (
        <DeviceType
          className={classNames({
            'max-w-[118px]': variant == 'detail',
          })}
          testDevice={testDevice}
        />
      )}

      {showParallelLine && <ParallelLine testDevice={testDevice} />}
    </div>
  );
};

const fetchAgentAndDevice = (
  setAgent,
  setAgentDevice,
  testDevice: TestDevice,
) => {
  if (!testDevice.isHybrid) return;
  const filterQuery = 'systemId:' + testDevice.targetMachine;
  AgentService.findAll(filterQuery)
    .then((res: Page<Agent>) => {
      if (!res.content?.length) return;
      setAgent(res.content[0]);
      const filterQuery = 'agentId:' + res.content[0].id;
      AgentDeviceService.findAll(filterQuery)
        .then((res: Page<AgentDevice>) => {
          if (!res.content?.length) return;
          setAgentDevice(res.content[0]);
        })
        .catch((e) => {
          console.log(e);
        });
    })
    .catch((e) => {
      console.log(e);
    });
};
